import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Building2, Briefcase, Clock, Users, KeyRound, ScrollText,
  Eye, Loader2, Trash2, FileText, UserCheck, AlertTriangle, Activity,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useActiveOrg } from "@/contexts/ActiveOrgContext";

interface OrgRow {
  id: string;
  name: string;
  type: "cabinet" | "entreprise";
  user_count: number;
}

export function SuperadminPanel() {
  const navigate = useNavigate();
  const { observeOrg } = useActiveOrg();
  const queryClient = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState<OrgRow | null>(null);
  const [confirmName, setConfirmName] = useState("");

  const { data: allOrgs = [], isLoading } = useQuery({
    queryKey: ["all-orgs-admin"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .order("name");
      if (error) throw error;
      return (data ?? []) as unknown as OrgRow[];
    },
  });

  const { data: stats } = useQuery({
    queryKey: ["admin-hub-stats"],
    queryFn: async () => {
      const [orgs, licences, activeLicences, logs, dossiers, members, pendingInvites] = await Promise.all([
        supabase.from("organizations").select("id, type", { count: "exact" }),
        supabase.from("licences").select("id, status, valid_until", { count: "exact" }),
        supabase.from("licences").select("id", { count: "exact", head: true }).eq("status", "active"),
        supabase.from("audit_logs").select("id", { count: "exact", head: true }),
        supabase.from("dossiers").select("id, status", { count: "exact" }),
        supabase.from("organization_members").select("user_id", { count: "exact" }),
        supabase.from("user_invitations").select("id", { count: "exact", head: true }).eq("status", "pending"),
      ]);

      const orgData = (orgs.data ?? []) as Array<{ type: string }>;
      const cabinetCount = orgData.filter(o => o.type === "cabinet").length;
      const entrepriseCount = orgData.filter(o => o.type === "entreprise").length;

      const dossierData = (dossiers.data ?? []) as Array<{ status: string }>;
      const dossiersByStatus = {
        brouillon: dossierData.filter(d => d.status === "brouillon").length,
        en_cours: dossierData.filter(d => d.status === "en_cours").length,
        valide: dossierData.filter(d => d.status === "valide").length,
        anomalie: dossierData.filter(d => d.status === "anomalie").length,
      };

      const licenceData = (licences.data ?? []) as Array<{ status: string; valid_until: string }>;
      const expiredLicences = licenceData.filter(l => l.status !== "active" || new Date(l.valid_until) < new Date()).length;

      return {
        totalOrgs: orgs.count ?? 0,
        cabinetCount,
        entrepriseCount,
        totalLicences: licences.count ?? 0,
        activeLicences: activeLicences.count ?? 0,
        expiredLicences,
        totalLogs: logs.count ?? 0,
        totalDossiers: dossiers.count ?? 0,
        dossiersByStatus,
        totalUsers: members.count ?? 0,
        pendingInvitations: pendingInvites.count ?? 0,
      };
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (orgId: string) => {
      const { error } = await supabase.from("organizations").delete().eq("id", orgId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Organisation supprimée");
      queryClient.invalidateQueries({ queryKey: ["all-orgs-admin"] });
      queryClient.invalidateQueries({ queryKey: ["admin-hub-stats"] });
      queryClient.invalidateQueries({ queryKey: ["org-memberships"] });
      setDeleteTarget(null);
      setConfirmName("");
    },
    onError: () => toast.error("Erreur lors de la suppression"),
  });

  return (
    <div className="space-y-6">
      {/* KPI Row 1 */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-primary" />
              </div>
              <span className="text-2xl font-bold">{stats?.totalOrgs ?? "–"}</span>
            </div>
            <p className="text-xs text-muted-foreground">Organisations</p>
            <Separator className="my-2" />
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {stats?.cabinetCount ?? 0} cabinets</span>
              <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {stats?.entrepriseCount ?? 0} entreprises</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-accent/15 flex items-center justify-center">
                <Users className="w-5 h-5 text-accent-foreground" />
              </div>
              <span className="text-2xl font-bold">{stats?.totalUsers ?? "–"}</span>
            </div>
            <p className="text-xs text-muted-foreground">Utilisateurs actifs</p>
            <Separator className="my-2" />
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {(stats?.pendingInvitations ?? 0) > 0 ? (
                <span className="flex items-center gap-1 text-warning"><Clock className="w-3 h-3" /> {stats?.pendingInvitations} invitation(s) en attente</span>
              ) : (
                <span>Aucune invitation en attente</span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-primary" />
              </div>
              <span className="text-2xl font-bold">{stats?.totalLicences ?? "–"}</span>
            </div>
            <p className="text-xs text-muted-foreground">Licences</p>
            <Separator className="my-2" />
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1 text-success"><UserCheck className="w-3 h-3" /> {stats?.activeLicences ?? 0} actives</span>
              {(stats?.expiredLicences ?? 0) > 0 && (
                <span className="flex items-center gap-1 text-destructive"><AlertTriangle className="w-3 h-3" /> {stats?.expiredLicences} expirées</span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                <FileText className="w-5 h-5 text-muted-foreground" />
              </div>
              <span className="text-2xl font-bold">{stats?.totalDossiers ?? "–"}</span>
            </div>
            <p className="text-xs text-muted-foreground">Dossiers</p>
            <Separator className="my-2" />
            <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
              {stats?.dossiersByStatus && (
                <>
                  {stats.dossiersByStatus.en_cours > 0 && <span className="flex items-center gap-1"><Activity className="w-3 h-3 text-primary" /> {stats.dossiersByStatus.en_cours} en cours</span>}
                  {stats.dossiersByStatus.valide > 0 && <span className="flex items-center gap-1 text-success">{stats.dossiersByStatus.valide} validés</span>}
                  {stats.dossiersByStatus.anomalie > 0 && <span className="flex items-center gap-1 text-destructive">{stats.dossiersByStatus.anomalie} anomalies</span>}
                  {stats.dossiersByStatus.brouillon > 0 && <span>{stats.dossiersByStatus.brouillon} brouillons</span>}
                  {stats.totalDossiers === 0 && <span>Aucun dossier</span>}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 2 - Audit + Quick Actions */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="pt-5 pb-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
              <ScrollText className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats?.totalLogs ?? "–"}</p>
              <p className="text-xs text-muted-foreground">Événements d'audit</p>
            </div>
          </CardContent>
        </Card>
        <Card className="flex items-center">
          <CardContent className="pt-5 pb-4 flex gap-3 w-full">
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/licences")} className="gap-2">
              <KeyRound className="w-4 h-4" /> Licences
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/audit-logs")} className="gap-2">
              <ScrollText className="w-4 h-4" /> Journal d'audit
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* All Orgs List */}
      <div>
        <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wider">
          Toutes les organisations ({allOrgs.length})
        </h3>
        {isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {allOrgs.map((org) => (
              <Card key={org.id} className="hover:border-primary/30 transition-colors">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={cn(
                      "w-9 h-9 rounded-lg flex items-center justify-center shrink-0",
                      org.type === "cabinet" ? "bg-primary/10" : "bg-accent/15"
                    )}>
                      {org.type === "cabinet"
                        ? <Briefcase className="w-4 h-4 text-primary" />
                        : <Building2 className="w-4 h-4 text-accent-foreground" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{org.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">{org.type} · {org.user_count} membres</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1.5 text-xs shrink-0"
                      onClick={() => { observeOrg(org.id); navigate("/dashboard"); }}
                    >
                      <Eye className="w-3.5 h-3.5" /> Observer
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(org)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setConfirmName(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer « {deleteTarget?.name} » ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est <strong>irréversible</strong>. Tous les dossiers, données et membres seront supprimés.
              <br /><br />
              Tapez <strong>{deleteTarget?.name}</strong> pour confirmer :
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={deleteTarget?.name} className="mt-2" />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setDeleteTarget(null); setConfirmName(""); }}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              disabled={confirmName !== deleteTarget?.name || deleteMutation.isPending}
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
